import { useGetBasketLazyQuery } from '@/generated/graphql'
import { getFirstGraphQLError } from '@/helpers/apollo/errors'

import { useCallback } from 'react'

export type BasketFetchOutcome =
  'idle' | 'loading' | 'basket' | 'unauthenticated' | 'forbidden' | 'gone' | 'failed'

/**
 * The one reading of a GetBasket result. Group share links get typed codes from the backend
 * (PT-1709): UNAUTHENTICATED = log in first, UNAUTHORIZED = another account's basket. Only an
 * explicit null basket is gone; any other error (server, network, gateway) is a plain failure.
 */
export const classifyBasketFetch = ({
  data,
  error,
  loading,
}: {
  data?: { basket?: unknown } | null
  error?: unknown
  loading: boolean
}): BasketFetchOutcome => {
  if (loading) return 'loading'
  if (data?.basket) return 'basket'
  if (data === undefined && error === undefined) return 'idle'
  if (error !== undefined) {
    const code = getFirstGraphQLError(error)?.extensions?.code
    if (code === 'UNAUTHENTICATED') return 'unauthenticated'
    if (code === 'UNAUTHORIZED') return 'forbidden'
    return 'failed'
  }
  return 'gone'
}

export const useGetBasketQuery = () => {
  const [run, result] = useGetBasketLazyQuery()

  const error =
    result.error ||
    (result.called && !result.loading && !result.data?.basket ? 'Unknown error' : null)

  const getBasket = useCallback(
    (basketId: string) => {
      void run({ variables: { id: basketId } })
    },
    [run],
  )

  return {
    run: getBasket,
    loading: result.loading,
    basket: result.data?.basket,
    called: result.called,
    error,
    outcome: classifyBasketFetch(result),
  }
}
